import { Loader2, MapPin } from "lucide-react";
import type { ReactNode } from "react";
import { formatApiError } from "../../../api";
import { GENDERS } from "../../../constants";
import type { MatchProfile, Profile } from "../../../types";
import { AppSelectField } from "../AppSelectField";
import { formatLocationLine } from "../locationUtils";

export function Field({ label, children, error }: { label: string; children: ReactNode; error?: string }) {
  return (
    <label className={`field${error ? " field--invalid" : ""}`}>
      <span>{label}</span>
      {children}
      {error ? (
        <span className="field-error" role="alert">
          {error}
        </span>
      ) : null}
    </label>
  );
}

export function SelectField<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string }[];
}) {
  return <AppSelectField label={label} value={value} onChange={onChange} options={options} />;
}

export function ErrorBanner({ error }: { error?: unknown }) {
  if (!error) return null;
  return <div className="banner error" role="alert">{formatApiError(error)}</div>;
}

export function profilePhotoItems(profile: MatchProfile | Profile) {
  return [...(profile.photos || [])].sort(
    (a, b) => Number(b.isProfilePicture) - Number(a.isProfilePicture) || a.order - b.order,
  );
}

export function ProfileFirstNameField({
  label,
  value,
  onChange,
  locked,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  locked: boolean;
  error?: string;
}) {
  if (locked) {
    return (
      <div className="field field--locked" aria-readonly="true">
        <span>{label}</span>
        <span className="field-static-value">{value || "—"}</span>
      </div>
    );
  }

  return (
    <Field label={label} error={error}>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete="given-name"
        data-profile-field="firstName"
        placeholder="Your first name"
        required
      />
    </Field>
  );
}

export function ProfileSection({
  title,
  description,
  mobile,
  children,
  className,
  sectionKey,
  invalid = false,
}: {
  title: string;
  description?: string;
  mobile: boolean;
  children: ReactNode;
  className?: string;
  sectionKey?: string;
  invalid?: boolean;
}) {
  if (mobile) {
    return (
      <section className={`preferences-section ${className || ""}`.trim()} data-profile-section={sectionKey}>
        <div className="preference-group-head">
          <span>{title}</span>
          {description ? <p>{description}</p> : null}
        </div>
        <div
          className={`preferences-group${invalid ? " preferences-group--invalid" : ""}`}
          aria-invalid={invalid || undefined}
        >
          {children}
        </div>
      </section>
    );
  }

  return (
    <div
      className={[
        "profile-form-section",
        invalid ? "profile-form-section--invalid" : "",
        className || "",
      ].filter(Boolean).join(" ")}
      data-profile-section={sectionKey}
      aria-invalid={invalid || undefined}
    >
      <div className="profile-form-section-head">
        <h3>{title}</h3>
        {description ? <p>{description}</p> : null}
      </div>
      {children}
    </div>
  );
}

export function ProfileFieldGroup({
  subhead,
  footnote,
  children,
  invalid = false,
}: {
  subhead?: string;
  footnote?: ReactNode;
  children: ReactNode;
  invalid?: boolean;
}) {
  return (
    <>
      {subhead ? <p className="preference-subhead">{subhead}</p> : null}
      <div
        className={`preferences-group${invalid ? " preferences-group--invalid" : ""}`}
        aria-invalid={invalid || undefined}
      >
        {children}
      </div>
      {footnote}
    </>
  );
}

export function ProfileLocationFields({
  profile,
  hasLocation,
  locationLoading,
  onLocate,
  integrated = false,
  error,
}: {
  profile: Partial<Profile>;
  hasLocation: boolean;
  locationLoading: boolean;
  onLocate: () => void;
  integrated?: boolean;
  error?: string;
}) {
  const locationLine = hasLocation
    ? formatLocationLine(profile.city, profile.state, profile.country)
    : null;

  const ariaLabel = locationLoading
    ? "Finding your city"
    : hasLocation
      ? `Location: ${locationLine}. Tap to update. Only city, state, and country are shared.`
      : "Use my location. Only city, state, and country are shared.";

  return (
    <>
      <button
        type="button"
        className={[
          "profile-location-control",
          integrated ? "profile-location-control--profile-field" : "",
          hasLocation ? "has-location" : "is-empty",
          locationLoading ? "is-loading" : "",
          error ? "profile-location-control--invalid" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        disabled={locationLoading}
        onClick={onLocate}
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
      >
      {locationLoading ? (
        <Loader2 className="spin profile-location-control-icon" size={18} aria-hidden="true" />
      ) : (
        <MapPin className="profile-location-control-icon" size={18} aria-hidden="true" />
      )}
      <span className="profile-location-control-body">
        {locationLoading ? (
          <>
            <span className="profile-location-control-primary">Finding your city…</span>
            <span className="profile-location-control-secondary">Only city, state, and country are shared</span>
          </>
        ) : hasLocation ? (
          <span className="profile-location-control-primary">{locationLine}</span>
        ) : (
          <>
            <span className="profile-location-control-primary">Use my location</span>
            <span className="profile-location-control-secondary">Tap to detect from this device</span>
          </>
        )}
      </span>
      {!locationLoading && hasLocation ? (
        <span className="profile-location-control-badge" aria-hidden="true">
          Update
        </span>
      ) : null}
      </button>
      {error ? (
        <span className="field-error profile-location-error" role="alert">
          {error}
        </span>
      ) : null}
    </>
  );
}

export function ProfileLocationPrivacyNote() {
  return (
    <p className="field-note field-note--location">
      Only your city, state, and country are shared — not your exact location.
    </p>
  );
}

export function IdentityFieldsNote() {
  return (
    <p className="field-note field-note--identity">
      Name, date of birth, and gender are locked after profile creation.
    </p>
  );
}

export function GenderField({
  value,
  onChange,
  locked,
  label = "Gender",
}: {
  value: "male" | "female";
  onChange: (value: "male" | "female") => void;
  locked: boolean;
  label?: string;
}) {
  const display = GENDERS.find((option) => option.value === value)?.label || value;

  if (locked) {
    return (
      <div className="field field--locked" aria-readonly="true">
        <span>{label}</span>
        <span className="field-static-value">{display}</span>
      </div>
    );
  }

  return (
    <SelectField label={label} value={value} onChange={onChange} options={GENDERS as any} />
  );
}
